/**
 * Fixed-timestep simulation with interpolated rendering.
 *
 * The bike sim is stiff (tire feedback gain scales with 1/speed) and must run at a
 * constant, small dt regardless of display refresh — a variable dt changes handling.
 * Render interpolates between the last two sim states so 60/120/144Hz all look smooth.
 */

export interface LoopCallbacks {
  /** Advance the simulation exactly `dt` seconds. */
  fixedUpdate(dt: number, simTime: number): void;
  /** Draw. `alpha` is the blend factor in [0,1) between previous and current sim state. */
  render(alpha: number, frameDt: number): void;
}

export interface LoopStats {
  fps: number;
  frameMs: number;
  simMs: number;
  renderMs: number;
  stepsLastFrame: number;
  simTime: number;
}

export const SIM_HZ = 240;
export const SIM_DT = 1 / SIM_HZ;
/** Never simulate more than this much wall time in one frame (spiral-of-death guard). */
const MAX_FRAME_TIME = 0.25;

export class GameLoop {
  readonly stats: LoopStats = {
    fps: 0,
    frameMs: 0,
    simMs: 0,
    renderMs: 0,
    stepsLastFrame: 0,
    simTime: 0,
  };

  private accumulator = 0;
  private lastTime = 0;
  private rafId = 0;
  private running = false;
  private fpsAccum = 0;
  private fpsFrames = 0;

  constructor(private readonly cb: LoopCallbacks) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    const tick = (now: number) => {
      if (!this.running) return;
      this.rafId = requestAnimationFrame(tick);
      this.frame(now);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  /** Run one frame manually — used by the headless capture harness for determinism. */
  stepManual(frameDt: number): void {
    this.accumulator += frameDt;
    let steps = 0;
    const t0 = performance.now();
    while (this.accumulator >= SIM_DT) {
      this.cb.fixedUpdate(SIM_DT, this.stats.simTime);
      this.stats.simTime += SIM_DT;
      this.accumulator -= SIM_DT;
      if (++steps > 600) {
        this.accumulator = 0;
        break;
      }
    }
    const t1 = performance.now();
    this.cb.render(this.accumulator / SIM_DT, frameDt);
    const t2 = performance.now();
    this.stats.stepsLastFrame = steps;
    this.stats.simMs = t1 - t0;
    this.stats.renderMs = t2 - t1;
    this.stats.frameMs = t2 - t0;
  }

  private frame(now: number): void {
    let frameTime = (now - this.lastTime) / 1000;
    this.lastTime = now;
    if (frameTime > MAX_FRAME_TIME) frameTime = MAX_FRAME_TIME;

    this.stepManual(frameTime);

    this.fpsAccum += frameTime;
    this.fpsFrames++;
    if (this.fpsAccum >= 0.5) {
      this.stats.fps = this.fpsFrames / this.fpsAccum;
      this.fpsAccum = 0;
      this.fpsFrames = 0;
    }
  }
}
